declare var google: any;

import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { AbntPipe } from '../../../../shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { RelatorioEntradasSaidasCategoriaService } from '../../../../shared/services/apis/financas/relatorio-entradas-saidas-categoria.service';
import { AuthService } from '../../../../shared/services/apis/usuario/auth.service';
import { MaskService } from '../../../../shared/services/mask.service';
import { RelatorioEntradasSaidasCategoriaRequest } from '../../../../shared/services/requests/financas/relatorio-entradas-saidas-categoria.request';
import { SaveRelatorioFiltroFavoritoRequest } from '../../../../shared/services/requests/financas/save-relatorio-filtro-favorito.request';
import { RelatorioFiltroFavorito } from '../../../../shared/services/responses/financas/relatorio-filtro-favorito.response';
import {
  RelatorioEntradasSaidasCategoriaItem,
  RelatorioEntradasSaidasCategoriaGrupoDetalhado,
} from '../../../../shared/services/responses/financas/relatorio-entradas-saidas-categoria.response';
import { NomeFavoritoModalComponent } from '../relatorio-entradas-saidas-cliente/components/nome-favorito-modal/nome-favorito-modal.component';

type ModoPeriodo = 'mes' | 'intervalo';

interface ContaOption {
  id: number;
  descricao: string;
}

@Component({
  selector: 'app-relatorio-entradas-saidas-categoria',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
    HeaderComponent,
    LoadingBoxComponent,
    AbntPipe,
  ],
  providers: [DatePipe],
  templateUrl: './relatorio-entradas-saidas-categoria.component.html',
  styleUrl: './relatorio-entradas-saidas-categoria.component.css',
})
export class RelatorioEntradasSaidasCategoriaComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Financas;
  public funcionalidadeEnum = FuncionalidadeEnum.Financas_Relatorios;

  public readonly exibirModoIntervalo = true;

  public readonly periodoOpcoes = [
    { id: 'mes' as const, descricao: 'do mês' },
    { id: 'intervalo' as const, descricao: 'intervalo' },
  ];

  public readonly CHART_COLORS = [
    '#4f86c6',
    '#5ab47e',
    '#f5c842',
    '#9b6bb5',
    '#e8734a',
    '#3dc9b0',
    '#f0a05a',
    '#e85a75',
    '#5c7db3',
    '#a3c654',
    '#63b3ed',
    '#68d391',
    '#f6e05e',
    '#b794f4',
    '#fc8181',
  ];

  public form = this.fb.group({
    modoPeriodo: ['mes' as ModoPeriodo, Validators.required],
    mesAno: [this.getMesAnoAtual(), Validators.required],
    dataInicial: [null as Date | null],
    dataFinal: [null as Date | null],
    idContaFinanceira: [null as number | null],
    favoritoSelecionado: [null as string | null],
  });

  public carregandoLookups = true;
  public carregandoRelatorio = false;
  public filtrosVisiveis = true;
  public consultaGerada = false;
  public possuiResultados = false;
  public mensagemEmptyState = '';

  public contas: ContaOption[] = [];
  public favoritos: RelatorioFiltroFavorito[] = [];
  public listaEntradas: RelatorioEntradasSaidasCategoriaItem[] = [];
  public listaSaidas: RelatorioEntradasSaidasCategoriaItem[] = [];
  public totalEntradas = 0;
  public totalSaidas = 0;
  public listaEntradasAgrupadas: RelatorioEntradasSaidasCategoriaGrupoDetalhado[] = [];
  public listaSaidasAgrupadas: RelatorioEntradasSaidasCategoriaGrupoDetalhado[] = [];
  public categoriasEntradasExpandidas = new Set<string>();
  public categoriasSaidasExpandidas = new Set<string>();

  private chartsReady = false;

  private readonly destroy$ = new Subject<void>();
  private readonly codigoRelatorio = 'ENTRADAS_SAIDAS_CATEGORIA';
  private readonly favoritosStorageKey = `financas-${this.codigoRelatorio.toLowerCase()}-favoritos`;
  private readonly idTipoRelatorioState = Number(window.history.state?.idTipoRelatorio || 0) || null;
  private idTipoRelatorio: number | null = null;
  /** Período da última consulta gerada (ex.: 03/2026), exibido no resultado. */
  public periodoDescricaoGerado = '';

  constructor(
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private alertService: AlertService,
    private relatorioService: RelatorioEntradasSaidasCategoriaService,
    private router: Router,
    private dialog: MatDialog,
    private authService: AuthService
  ) {}

  public ngOnInit(): void {
    if (!this.exibirModoIntervalo) {
      this.form.patchValue({ modoPeriodo: 'mes' });
    }
    this.carregarLookups();
    this.configurarMudancaPeriodo();
    this.carregarGoogleCharts();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public voltar(): void {
    this.router.navigate(['/financas/relatorios']);
  }

  // ─── Getters ───────────────────────────────────────────────────────────────

  public get isModoMes(): boolean {
    return this.form.get('modoPeriodo')?.value === 'mes';
  }

  public get mesAnoControl(): FormControl<string> {
    return this.form.get('mesAno') as FormControl<string>;
  }

  public get dataInicialControl(): FormControl<Date | null> {
    return this.form.get('dataInicial') as FormControl<Date | null>;
  }

  public get dataFinalControl(): FormControl<Date | null> {
    return this.form.get('dataFinal') as FormControl<Date | null>;
  }

  public get hasFavoritoSelecionado(): boolean {
    return !!this.favoritoSelecionadoAtual;
  }

  public get hasAlgumFiltroPreenchido(): boolean {
    const idConta = this.form.get('idContaFinanceira')?.value ?? null;
    if (idConta) return true;
    if (this.form.get('modoPeriodo')?.value === 'intervalo') {
      return !!(this.form.get('dataInicial')?.value || this.form.get('dataFinal')?.value);
    }
    return false;
  }

  public get favoritoSelecionadoAtual(): RelatorioFiltroFavorito | null {
    const id = this.form.get('favoritoSelecionado')?.value;
    return this.favoritos.find(f => String(f.idRelatorioFiltroFavorito) === id) || null;
  }

  // ─── Expandable detail helpers ─────────────────────────────────────────────

  public toggleCategoriaExpandida(tipo: 'entrada' | 'saida', categoria: string): void {
    const set = tipo === 'entrada' ? this.categoriasEntradasExpandidas : this.categoriasSaidasExpandidas;
    if (set.has(categoria)) set.delete(categoria);
    else set.add(categoria);
  }

  public isCategoriaExpandida(tipo: 'entrada' | 'saida', categoria: string): boolean {
    const set = tipo === 'entrada' ? this.categoriasEntradasExpandidas : this.categoriasSaidasExpandidas;
    return set.has(categoria);
  }

  public getMovimentacoesCategoria(tipo: 'entrada' | 'saida', categoria: string) {
    const lista = tipo === 'entrada' ? this.listaEntradasAgrupadas : this.listaSaidasAgrupadas;
    const grupo = lista.find(g => g.categoria === categoria);
    if (!grupo?.movimentacoes?.length) return [];
    return [...grupo.movimentacoes].sort(
      (a, b) => new Date(a.dataMovimentacao).getTime() - new Date(b.dataMovimentacao).getTime()
    );
  }

  // ─── Chart helpers ─────────────────────────────────────────────────────────

  public chartColor(index: number): string {
    return this.CHART_COLORS[index % this.CHART_COLORS.length];
  }

  private carregarGoogleCharts(): void {
    const doLoad = () => {
      google.charts.load('current', { packages: ['corechart'], language: 'pt' });
      google.charts.setOnLoadCallback(() => {
        this.chartsReady = true;
        if (this.possuiResultados) {
          setTimeout(() => this.desenharGraficos(), 50);
        }
      });
    };

    if (typeof google !== 'undefined') {
      doLoad();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.gstatic.com/charts/loader.js';
    script.onload = () => doLoad();
    document.head.appendChild(script);
  }

  private desenharGraficos(): void {
    const isDark = document.body.classList.contains('dark-mode');
    const borderColor = isDark ? '#1B1B1B' : '#ffffff';

    const draw = (lista: RelatorioEntradasSaidasCategoriaItem[], elementId: string) => {
      const el = document.getElementById(elementId);
      if (!el || !lista.length) return;

      const colors = lista.map((_, i) => this.CHART_COLORS[i % this.CHART_COLORS.length]);

      const dt = new google.visualization.DataTable();
      dt.addColumn('string', 'Categoria');
      dt.addColumn('number', 'Valor');
      dt.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });

      dt.addRows(
        lista.map((item, i) => [
          item.categoria,
          item.valor,
          `<div style="padding:10px 14px;font-family:inherit;min-width:170px;">
                    <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
                        <span style="width:10px;height:10px;border-radius:50%;background:${colors[i]};flex-shrink:0;display:inline-block;"></span>
                        <strong style="font-size:13px;color:#1e293b;">${item.categoria}</strong>
                    </div>
                    <div style="font-size:13px;color:#3c4043;padding-left:18px;">${this.formatarMoeda(item.valor)}</div>
                    <div style="font-size:12px;color:#94a3b8;padding-left:18px;">${this.formatarPercentual(item.percentual)}%</div>
                </div>`,
        ])
      );

      const options = {
        pieHole: 0.65,
        legend: 'none',
        colors,
        pieSliceBorderColor: borderColor,
        backgroundColor: 'transparent',
        chartArea: { left: '5%', top: '5%', width: '90%', height: '90%' },
        pieSliceText: 'none',
        tooltip: { isHtml: true, trigger: 'focus' },
        animation: { startup: true, duration: 700, easing: 'out' },
      };

      new google.visualization.PieChart(el).draw(dt, options);
    };

    draw(this.listaEntradas, 'chart_entradas');
    draw(this.listaSaidas, 'chart_saidas');
  }

  // ─── Period ────────────────────────────────────────────────────────────────

  public selecionarModoPeriodo(modo: ModoPeriodo): void {
    this.form.patchValue({ modoPeriodo: modo });
  }

  public onDateInput(event: Event, controlName: 'dataInicial' | 'dataFinal'): void {
    MaskService.onDateInput(event, this.form.get(controlName));
  }

  public navegarMes(passos: number): void {
    const ref = this.parseMesAno(this.mesAnoControl.value) ?? new Date();
    ref.setMonth(ref.getMonth() + passos);
    this.mesAnoControl.setValue(this.formatMesAno(ref));
  }

  public normalizarMesAno(): void {
    const norm = this.parseMesAno(this.mesAnoControl.value);
    this.mesAnoControl.setValue(norm ? this.formatMesAno(norm) : this.getMesAnoAtual());
  }

  // ─── Favorites ─────────────────────────────────────────────────────────────

  public favoritarFiltros(): void {
    if (this.hasFavoritoSelecionado) {
      this.removerFavoritoSelecionado();
      return;
    }

    const dialogRef = this.dialog.open(NomeFavoritoModalComponent, {
      width: '480px',
      maxWidth: '95vw',
      autoFocus: false,
      data: { nomeInicial: this.getProximoNomeFavorito() },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((nome: string | undefined) => {
        if (!nome?.trim()) return;
        this.salvarFavorito(nome.trim());
      });
  }

  public carregarFavoritoSelecionado(): void {
    const id = this.form.get('favoritoSelecionado')?.value;
    if (!id) {
      this.limparFiltrosFavorito();
      return;
    }

    const favorito = this.favoritos.find(f => String(f.idRelatorioFiltroFavorito) === id);
    if (!favorito) {
      this.limparFiltrosFavorito();
      return;
    }

    if (!this.exibirModoIntervalo) {
      let mesAno = favorito.filtro.mesAno ?? this.getMesAnoAtual();
      if (favorito.filtro.modoPeriodo === 'intervalo' && favorito.filtro.dataInicial) {
        const d = new Date(favorito.filtro.dataInicial);
        if (!isNaN(d.getTime())) {
          mesAno = `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
        }
      }
      this.form.patchValue({
        modoPeriodo: 'mes',
        mesAno,
        dataInicial: null,
        dataFinal: null,
        idContaFinanceira: favorito.filtro.idContaFinanceira,
      });
      return;
    }

    this.form.patchValue({
      modoPeriodo: (favorito.filtro.modoPeriodo === 'intervalo' ? 'intervalo' : 'mes') as ModoPeriodo,
      mesAno: favorito.filtro.mesAno ?? this.getMesAnoAtual(),
      dataInicial: favorito.filtro.dataInicial ? new Date(favorito.filtro.dataInicial) : null,
      dataFinal: favorito.filtro.dataFinal ? new Date(favorito.filtro.dataFinal) : null,
      idContaFinanceira: favorito.filtro.idContaFinanceira,
    });
  }

  public getFavoritoValue(favorito: RelatorioFiltroFavorito): string {
    return String(favorito.idRelatorioFiltroFavorito);
  }

  // ─── Lookup ────────────────────────────────────────────────────────────────

  public carregarLookups(): void {
    this.carregandoLookups = true;
    this.relatorioService
      .getLookup()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.contas = (response.listaContaFinanceira || [])
            .map(item => ({ id: Number(item.id), descricao: item.descricao }))
            .sort((a, b) => a.descricao.localeCompare(b.descricao));

          this.idTipoRelatorio = response.idTipoRelatorio ?? this.idTipoRelatorioState;
          this.favoritos = response.listaFavoritos || [];
          if (!this.idTipoRelatorio) this.carregarFavoritosLocalStorage();

          this.carregandoLookups = false;
        },
        error: () => {
          this.carregandoLookups = false;
          this.alertService.show('Erro', 'Erro ao carregar os filtros do relatório.', AlertType.error);
        },
      });
  }

  // ─── Generate ──────────────────────────────────────────────────────────────

  public gerar(): void {
    if (!this.validarPeriodo()) return;

    const { inicio, fim } = this.getPeriodoSelecionado();
    if (!inicio || !fim) {
      this.alertService.show(
        'Atenção',
        'Informe um período válido para gerar o relatório.',
        AlertType.warning
      );
      return;
    }

    this.periodoDescricaoGerado = this.getPeriodoDescricao();
    this.consultaGerada = true;
    this.carregandoRelatorio = true;
    this.mensagemEmptyState = '';
    this.categoriasEntradasExpandidas.clear();
    this.categoriasSaidasExpandidas.clear();

    const request: RelatorioEntradasSaidasCategoriaRequest = {
      dataInicio: this.datePipe.transform(inicio, 'yyyy-MM-dd') || '',
      dataFim: this.datePipe.transform(fim, 'yyyy-MM-dd') || '',
      idContaFinanceira: this.form.get('idContaFinanceira')?.value ?? null,
    };

    this.relatorioService
      .getPreview(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.totalEntradas = Number(response.totalEntradas || 0);
          this.totalSaidas = Number(response.totalSaidas || 0);
          this.listaEntradas = (response.listaEntradas || []).map(i => ({
            ...i,
            valor: Number(i.valor || 0),
            percentual: Number(i.percentual || 0),
          }));
          this.listaSaidas = (response.listaSaidas || []).map(i => ({
            ...i,
            valor: Number(i.valor || 0),
            percentual: Number(i.percentual || 0),
          }));
          this.listaEntradasAgrupadas = (response.listaEntradasAgrupadas || []).map(g => ({
            ...g,
            subtotal: Number(g.subtotal || 0),
            movimentacoes: (g.movimentacoes || []).map(m => ({ ...m, valor: Number(m.valor || 0) })),
          }));
          this.listaSaidasAgrupadas = (response.listaSaidasAgrupadas || []).map(g => ({
            ...g,
            subtotal: Number(g.subtotal || 0),
            movimentacoes: (g.movimentacoes || []).map(m => ({ ...m, valor: Number(m.valor || 0) })),
          }));
          this.possuiResultados = this.listaEntradas.length > 0 || this.listaSaidas.length > 0;
          this.filtrosVisiveis = !this.possuiResultados;
          this.mensagemEmptyState = this.possuiResultados
            ? ''
            : 'A consulta não retornou nenhum registro. Tente refazer a consulta com outros parâmetros.';
          this.carregandoRelatorio = false;
          if (this.possuiResultados && this.chartsReady) {
            setTimeout(() => this.desenharGraficos(), 50);
          }
        },
        error: () => {
          this.listaEntradas = [];
          this.listaSaidas = [];
          this.totalEntradas = 0;
          this.totalSaidas = 0;
          this.listaEntradasAgrupadas = [];
          this.listaSaidasAgrupadas = [];
          this.possuiResultados = false;
          this.filtrosVisiveis = true;
          this.carregandoRelatorio = false;
          this.alertService.show('Erro', 'Erro ao gerar o relatório.', AlertType.error);
        },
      });
  }

  public toggleFiltros(): void {
    this.filtrosVisiveis = !this.filtrosVisiveis;
  }

  // ─── Export ────────────────────────────────────────────────────────────────

  public imprimir(): void {
    if (!this.possuiResultados) return;

    const periodo = this.periodoDescricaoGerado;
    const geradoEm = this.datePipe.transform(new Date(), 'dd/MM/yyyy HH:mm') || '';
    const empresa = this.authService.getUserInfos().nomeEmpresa;

    const totalLabel = (secao: string) => (secao === 'Entradas' ? 'Total de Entradas' : 'Total de Saídas');
    const buildTableHtml = (titulo: string, lista: RelatorioEntradasSaidasCategoriaItem[], total: number) => `
            <section class="grupo">
                <h2>${titulo}</h2>
                <table>
                    <thead><tr><th>Categoria</th><th class="valor">Valor</th><th class="valor">%</th></tr></thead>
                    <tbody>
                        ${lista
                          .map(
                            item => `
                            <tr>
                                <td>${item.categoria}</td>
                                <td class="valor">${this.formatarMoeda(item.valor)}</td>
                                <td class="valor">${this.formatarPercentual(item.percentual)}%</td>
                            </tr>`
                          )
                          .join('')}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>${totalLabel(titulo)}</strong></td>
                            <td class="valor"><strong>${this.formatarMoeda(total)}</strong></td>
                            <td class="valor"><strong>100,0%</strong></td>
                        </tr>
                    </tfoot>
                </table>
            </section>`;

    const conteudo = `
            <html>
            <head>
                <title>Relatório de Entrada/Saída por Categoria</title>
                <style>
                    body { font-family: Arial, sans-serif; color: #1f2937; padding: 24px; }
                    h1 { margin: 0 0 8px; font-size: 22px; }
                    .meta { margin-bottom: 24px; color: #475569; font-size: 12px; }
                    .grupo { margin-bottom: 28px; page-break-inside: avoid; }
                    .grupo h2 { font-size: 16px; margin: 0 0 10px; text-transform: uppercase; color: #374151; }
                    table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
                    thead { display: table-header-group; }
                    th, td { border-bottom: 1px solid #e5e7eb; padding: 10px 8px; text-align: left; }
                    th.valor, td.valor, tfoot td.valor { text-align: right; }
                    tfoot tr { border-top: 2px solid #d1d5db; }
                    tfoot td { font-weight: 600; }
                    @media print { @page { margin: 1.5cm; } }
                </style>
            </head>
            <body>
                <h1>Relatório de Entradas e Saídas por Categoria</h1>
                <div class="meta">
                    <div>Período: ${periodo}</div>
                    <div>Gerado em: ${geradoEm}</div>
                    <div>Empresa: ${empresa}</div>
                </div>
                ${buildTableHtml('Entradas', this.listaEntradas, this.totalEntradas)}
                ${buildTableHtml('Saídas', this.listaSaidas, this.totalSaidas)}
            </body>
            </html>`;

    const popup = window.open('', '_blank', 'width=1200,height=900');
    if (!popup) return;
    popup.document.write(conteudo);
    popup.document.close();
    popup.focus();
    popup.print();
  }

  public downloadCsv(): void {
    if (!this.possuiResultados) return;

    const sep = ';';
    const header = ['Categoria', 'Valor', 'Porcentagem %'].join(sep);
    const toRow = (item: RelatorioEntradasSaidasCategoriaItem) =>
      [
        item.categoria,
        item.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        item.percentual.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      ].join(sep);

    const linhas = [
      header,
      'Entradas',
      ...(this.listaEntradas.length ? this.listaEntradas.map(toRow) : ['']),
      '',
      'Saídas',
      ...(this.listaSaidas.length ? this.listaSaidas.map(toRow) : ['']),
    ];

    const csv = '\uFEFF' + linhas.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'relatorio-entradas-saidas-por-categoria.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  // ─── Formatting ────────────────────────────────────────────────────────────

  public formatarMoeda(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  public formatarPercentual(value: number): string {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
  }

  public formatarData(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '—' : this.datePipe.transform(d, 'dd/MM/yyyy') || '—';
  }

  public stripHtml(value: string | null | undefined): string {
    if (!value) return '—';
    const clean = value
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return clean || '—';
  }

  public formatarMoedaCompact(value: number): string {
    const abs = Math.abs(value);
    if (abs >= 1_000_000_000) return `R$ ${(value / 1_000_000_000).toFixed(1)}B`;
    if (abs >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `R$ ${(value / 1_000).toFixed(1)}k`;
    return this.formatarMoeda(value);
  }

  public getPeriodoDescricao(): string {
    if (this.isModoMes) return this.mesAnoControl.value;
    const inicio = this.dataInicialControl.value
      ? this.datePipe.transform(this.dataInicialControl.value, 'dd/MM/yyyy')
      : '';
    const fim = this.dataFinalControl.value
      ? this.datePipe.transform(this.dataFinalControl.value, 'dd/MM/yyyy')
      : '';
    return `${inicio} a ${fim}`;
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  private configurarMudancaPeriodo(): void {
    this.form
      .get('modoPeriodo')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(modo => {
        if (!modo) return;
        if (modo === 'mes') {
          this.dataInicialControl.setValue(null);
          this.dataFinalControl.setValue(null);
        } else {
          this.mesAnoControl.setValue(this.getMesAnoAtual());
        }
      });
  }

  private validarPeriodo(): boolean {
    if (this.isModoMes) {
      if (!this.parseMesAno(this.mesAnoControl.value)) {
        this.alertService.show('Atenção', 'Informe um mês/ano válido no formato MM/AAAA.', AlertType.warning);
        return false;
      }
      return true;
    }
    const ini = this.dataInicialControl.value;
    const fim = this.dataFinalControl.value;
    if (!ini || !fim) {
      this.alertService.show('Atenção', 'Informe a data inicial e a data final.', AlertType.warning);
      return false;
    }
    if (fim < ini) {
      this.alertService.show(
        'Atenção',
        'A data final não pode ser anterior à data inicial.',
        AlertType.warning
      );
      return false;
    }
    return true;
  }

  private getPeriodoSelecionado(): { inicio: Date | null; fim: Date | null } {
    if (this.isModoMes) {
      const ref = this.parseMesAno(this.mesAnoControl.value);
      if (!ref) return { inicio: null, fim: null };
      const inicio = new Date(ref.getFullYear(), ref.getMonth(), 1);
      const fim = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
      fim.setHours(23, 59, 59, 999);
      return { inicio, fim };
    }
    const inicio = this.dataInicialControl.value ? new Date(this.dataInicialControl.value) : null;
    const fim = this.dataFinalControl.value ? new Date(this.dataFinalControl.value) : null;
    if (fim) fim.setHours(23, 59, 59, 999);
    return { inicio, fim };
  }

  private limparFiltrosFavorito(): void {
    this.form.patchValue({
      favoritoSelecionado: null,
      modoPeriodo: 'mes',
      mesAno: this.getMesAnoAtual(),
      dataInicial: null,
      dataFinal: null,
      idContaFinanceira: null,
    });
  }

  private carregarFavoritosLocalStorage(): void {
    try {
      const raw = localStorage.getItem(this.favoritosStorageKey);
      this.favoritos = raw ? (JSON.parse(raw) as RelatorioFiltroFavorito[]) : [];
    } catch {
      this.favoritos = [];
    }
  }

  private persistirFavoritos(): void {
    if (this.idTipoRelatorio) return;
    localStorage.setItem(this.favoritosStorageKey, JSON.stringify(this.favoritos));
  }

  private getProximoNomeFavorito(): string {
    const numeros = this.favoritos
      .map(f => /^Favorito (\d+)$/.exec(f.nome)?.[1])
      .filter((v): v is string => !!v)
      .map(Number)
      .filter(v => !Number.isNaN(v));
    const next = numeros.length ? Math.max(...numeros) + 1 : this.favoritos.length + 1;
    return `Favorito ${next}`;
  }

  private salvarFavorito(nome: string): void {
    const filtro = {
      modoPeriodo: this.form.get('modoPeriodo')?.value || 'mes',
      mesAno: this.mesAnoControl.value,
      dataInicial: this.dataInicialControl.value
        ? this.datePipe.transform(this.dataInicialControl.value, 'yyyy-MM-dd')
        : null,
      dataFinal: this.dataFinalControl.value
        ? this.datePipe.transform(this.dataFinalControl.value, 'yyyy-MM-dd')
        : null,
      idCliente: null,
      clienteDescricao: null,
      idContaFinanceira: this.form.get('idContaFinanceira')?.value ?? null,
      idCategoriaFinanceira: null,
    };

    if (!this.idTipoRelatorio) {
      const favorito = {
        idRelatorioFiltroFavorito: Date.now(),
        idTipoRelatorio: 0,
        nome,
        isPadrao: false,
        datetimeInclusao: new Date().toISOString(),
        filtro,
      } as RelatorioFiltroFavorito;
      this.favoritos = [favorito, ...this.favoritos];
      this.persistirFavoritos();
      this.form.patchValue({ favoritoSelecionado: String(favorito.idRelatorioFiltroFavorito) });
      return;
    }

    const request: SaveRelatorioFiltroFavoritoRequest = {
      idTipoRelatorio: this.idTipoRelatorio,
      nome,
      isPadrao: false,
      filtro,
    };
    this.relatorioService
      .saveFavorito(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.isError || !response.favorito) {
            this.alertService.show(
              'Erro',
              response.errorDescription || 'Erro ao salvar favorito.',
              AlertType.error
            );
            return;
          }
          const favorito = response.favorito;
          this.favoritos = [
            favorito,
            ...this.favoritos.filter(f => f.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito),
          ].sort((a, b) => a.nome.localeCompare(b.nome));
          this.form.patchValue({ favoritoSelecionado: String(favorito.idRelatorioFiltroFavorito) });
        },
        error: () => this.alertService.show('Erro', 'Erro ao salvar favorito.', AlertType.error),
      });
  }

  private removerFavoritoSelecionado(): void {
    const favorito = this.favoritoSelecionadoAtual;
    if (!favorito) return;

    if (!this.idTipoRelatorio) {
      this.favoritos = this.favoritos.filter(
        f => f.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito
      );
      this.persistirFavoritos();
      this.form.patchValue({ favoritoSelecionado: null });
      return;
    }

    this.relatorioService
      .deleteFavorito(favorito.idRelatorioFiltroFavorito)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.isError) {
            this.alertService.show(
              'Erro',
              response.errorDescription || 'Erro ao remover favorito.',
              AlertType.error
            );
            return;
          }
          this.favoritos = this.favoritos.filter(
            f => f.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito
          );
          this.form.patchValue({ favoritoSelecionado: null });
        },
        error: () => this.alertService.show('Erro', 'Erro ao remover favorito.', AlertType.error),
      });
  }

  private getMesAnoAtual(): string {
    return this.formatMesAno(new Date());
  }

  private formatMesAno(date: Date): string {
    return `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  }

  private parseMesAno(value: string | null | undefined): Date | null {
    const match = (value || '').trim().match(/^(\d{2})\/(\d{4})$/);
    if (!match) return null;
    const mes = Number(match[1]);
    const ano = Number(match[2]);
    if (mes < 1 || mes > 12) return null;
    return new Date(ano, mes - 1, 1);
  }
}
